import Vue from "vue";
import ApiService from "../../services/ApiService";
import { EventBus, EVENTS } from "../../services/EventBus";
import { mapGetters } from "vuex";

Vue.component("vote-widget", {
  delimiters: ["${", "}"],

  props: {
    referenceType: {
      type: String,
      default: "homepage",
    },
    referenceId: {
      type: Number,
      default: 0,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "Wie findest du das?",
    },
    // Neue Props für initiale Daten aus Item-Properties
    initialThumbsUp: {
      type: Number,
      default: null,
    },
    initialThumbsDown: {
      type: Number,
      default: null,
    },
    // Deaktiviert Abstimmungen (z.B. bei geschlossenen Gerüchten)
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      thumbsUp: 0,
      thumbsDown: 0,
      displayedThumbsUp: 0,
      displayedThumbsDown: 0,
      total: 0,
      userVote: null, // "up", "down" oder null
      isLoading: false,
      message: "",
      messageType: "success", // "success", "warning", "danger"
      pulsingUp: false,
      pulsingDown: false,
    };
  },

  computed: {
    ...mapGetters(["isLoggedIn"]),
    
    thumbsUpActive() {
      return this.userVote === "up";
    },
    thumbsDownActive() {
      return this.userVote === "down";
    },
  },

  watch: {
    isLoggedIn(newVal) {
      if (newVal && this.initialThumbsUp !== null && this.initialThumbsDown !== null) {
        // User hat sich eingeloggt, lade seine Vote-Wahl
        this.loadUserVote();
      }
    },
  },

  created() {
  },

  mounted() {
    // Only load in browser, not during SSR
    if (typeof window !== "undefined") {
      // Prüfe ob initiale Daten als Props übergeben wurden
      if (this.initialThumbsUp !== null && this.initialThumbsDown !== null) {
        // Nutze die übergebenen Daten, KEIN API-Call für Counts nötig
        this.thumbsUp = this.initialThumbsUp;
        this.thumbsDown = this.initialThumbsDown;
        this.displayedThumbsUp = this.initialThumbsUp;
        this.displayedThumbsDown = this.initialThumbsDown;
        this.total = this.initialThumbsUp + this.initialThumbsDown;
        
        // Wenn User eingeloggt ist, lade seine Vote-Wahl
        // (für "Deine Wahl" Anzeige und Button-Highlighting)
        if (this.isLoggedIn) {
          this.loadUserVote();
        }
      } else {
        // Keine initialen Daten vorhanden (sehr selten), lade alles via API
        this.loadVotes();
      }
    }
  },

  methods: {
    loadVotes() {
      this.isLoading = true;

      ApiService.get("/rest/fanliebling/votes", {
        referenceType: this.referenceType,
        referenceId: this.referenceId,
      })
        .done((response) => {
          this.thumbsUp = response.thumbsUp || 0;
          this.thumbsDown = response.thumbsDown || 0;
          this.total = response.total || 0;
          this.userVote = response.userVote || null;
          
          // Initiale Werte für Animation setzen
          this.displayedThumbsUp = this.thumbsUp;
          this.displayedThumbsDown = this.thumbsDown;
        })
        .fail((error) => {
          console.error("Fehler beim Laden der Votes:", error);
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    // Lädt nur die User-spezifische Vote-Wahl (für optimierte Performance)
    loadUserVote() {
      if (!this.isLoggedIn) {
        return;
      }

      ApiService.get("/rest/fanliebling/votes", {
        referenceType: this.referenceType,
        referenceId: this.referenceId,
      })
        .done((response) => {
          this.userVote = response.userVote || null;
        })
        .fail((error) => {
          // Stiller Fehler - keine Notification nötig
        });
    },

    openLoginModal() {
      // Öffne das Login Modal
      const loginButton = document.querySelector('.control-user a[data-toggle="modal"]');
      if (loginButton) {
        loginButton.click();
      }
    },

    vote(voteType) {
      if (this.isLoading || this.disabled) {
        return;
      }

      // Prüfe Login-Status vor dem Voting
      if (!this.isLoggedIn) {
        this.openLoginModal();
        return;
      }

      this.isLoading = true;
      this.message = "";

      ApiService.post("/rest/fanliebling/vote", {
        voteType: voteType,
        referenceType: this.referenceType,
        referenceId: this.referenceId,
      }, {
        supressNotifications: true // Unterdrücke globale Fehler-Notifications
      })
        .done((response) => {
          // Backend sendet jetzt: {data: {...}}
          const data = response.data || response;

          if (data && data.thumbsUp !== undefined) {
            const oldThumbsUp = this.thumbsUp;
            const oldThumbsDown = this.thumbsDown;
            const newThumbsUp = parseInt(data.thumbsUp) || 0;
            const newThumbsDown = parseInt(data.thumbsDown) || 0;
            
            // Vue reactivity mit $set sicherstellen
            this.$set(this, "thumbsUp", newThumbsUp);
            this.$set(this, "thumbsDown", newThumbsDown);
            this.$set(this, "total", parseInt(data.total) || 0);
            this.$set(this, "userVote", data.userVote || null);

            // Animiere die Werte
            if (voteType === 'up') {
              this.pulsingUp = true;
              this.animateValue('up', oldThumbsUp, newThumbsUp, 600);
              setTimeout(() => { this.pulsingUp = false; }, 600);
            } else {
              this.pulsingDown = true;
              this.animateValue('down', oldThumbsDown, newThumbsDown, 600);
              setTimeout(() => { this.pulsingDown = false; }, 600);
            }

            this.message = "Vielen Dank für deine Stimme";
            this.messageType = "success";

            // Event auslösen für Badge-Update
            EventBus.$emit(EVENTS.INTERACTION_COMPLETED);

            // Nachricht nach 3 Sekunden ausblenden
            setTimeout(() => {
              this.message = "";
            }, 3000);
          } else {
            this.message = "Ein Fehler ist aufgetreten.";
            this.messageType = "danger";
          }
        })
        .fail((response, statusCode) => {
          // ApiService gibt (response, statusCode) zurück, nicht jqXHR
          console.error("Vote Error:", response);
          console.log("Vote Error Details:", {
            statusCode: statusCode,
            response: response
          });
          
          // Prüfe auf spezifische Fehlercodes
          if (statusCode === 401) {
            this.message = "Bitte melde dich an, um zu bewerten.";
            this.messageType = "warning";
            this.openLoginModal();
            
            // Nachricht nach 3 Sekunden ausblenden
            setTimeout(() => {
              this.message = "";
            }, 3000);
          } else if (statusCode === 403) {
            // Bereits gevotet - extrahiere Nachricht aus verschiedenen möglichen Strukturen
            if (response) {
              // Struktur: {error: {message: "..."}}
              if (response.error && response.error.message) {
                this.message = response.error.message;
              } 
              // Struktur: {message: "..."}
              else if (response.message) {
                this.message = response.message;
              } 
              // Fallback
              else {
                this.message = "Du hast bereits f\u00fcr diesen Spieler abgestimmt.";
              }
            } else {
              this.message = "Du hast bereits f\u00fcr diesen Spieler abgestimmt.";
            }
            this.messageType = "warning";
            
            // Nachricht nach 3 Sekunden ausblenden
            setTimeout(() => {
              this.message = "";
            }, 3000);
          } else if (response && response.error && response.error.message) {
            // Andere Fehler mit error.message aus Backend
            this.message = response.error.message;
            this.messageType = "danger";
          } else if (response && response.message) {
            // Fallback: message direkt in response
            this.message = response.message;
            this.messageType = "danger";
          } else {
            this.message = "Bewertung fehlgeschlagen. Bitte versuche es erneut.";
            this.messageType = "danger";
          }
          
          // Lade aktuelle Votes neu
          setTimeout(() => {
            this.loadVotes();
          }, 1000);
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    thumbsUpClick() {
      this.vote("up");
    },

    thumbsDownClick() {
      this.vote("down");
    },
    
    // Animiert einen Wert von start zu end über eine bestimmte Dauer
    animateValue(type, start, end, duration) {
      const startTime = performance.now();
      const difference = end - start;
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease out)
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        const currentValue = start + (difference * easeProgress);
        
        if (type === 'up') {
          this.displayedThumbsUp = currentValue;
        } else {
          this.displayedThumbsDown = currentValue;
        }
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          if (type === 'up') {
            this.displayedThumbsUp = end;
          } else {
            this.displayedThumbsDown = end;
          }
        }
      };
      
      requestAnimationFrame(animate);
    },
  },

  render: function(h) {
    return h("div", { class: "vote-widget mb-3" }, [
      this.showTitle && this.title
        ? h("div", { class: "card-header bg-custom text-white" }, [
            h("h4", { class: "mb-0" }, this.title),
          ])
        : null,
        
        h(
            "div",
            { class: "my-2 text-center font-weight-bold" },
            "Würde der Spieler passen?"
        ),

      h("div", { class: "card-body text-center p-0" }, [
        this.message
          ? h("div", { 
              class: `alert alert-${this.messageType} mb-2 py-2 px-3`,
              style: { fontSize: "0.85rem" }
            }, this.message)
          : null,

        h("div", { 
          class: "vote-buttons d-flex justify-content-center",
          style: { gap: "1rem" }
        }, [
          h(
            "button",
            {
              class: {
                "btn": true,
                "btn-lg": true,
                "btn-outline-success": !this.thumbsUpActive,
                "vote-pulse-animation": this.pulsingUp,
              },
              style: {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: "100px",
                backgroundColor: this.thumbsUpActive ? "#28a745" : "",
                borderColor: this.thumbsUpActive ? "#28a745" : "",
                color: this.thumbsUpActive ? "var(--color-text)" : "",
              },
              attrs: {
                disabled: this.isLoading || this.disabled,
              },
              on: {
                click: this.thumbsUpClick,
              },
            },
            [
              h("i", { 
                class: "fa fa-thumbs-up fa-lg",
                style: {
                  color: this.thumbsUpActive ? "var(--color-text)" : "",
                }
              }),
              h("span", { 
                style: { 
                  fontSize: "1.25rem", 
                  fontWeight: "bold",
                  color: this.thumbsUpActive ? "var(--color-text)" : "",
                } 
              }, `${Math.round(this.displayedThumbsUp)}`),
            ]
          ),

          h(
            "button",
            {
              class: {
                "btn": true,
                "btn-lg": true,
                "btn-outline-danger": !this.thumbsDownActive,
                "vote-pulse-animation": this.pulsingDown,
              },
              style: {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: "100px",
                justifyContent: "center",
                backgroundColor: this.thumbsDownActive ? "#dc3545" : "",
                borderColor: this.thumbsDownActive ? "#dc3545" : "",
                color: this.thumbsDownActive ? "var(--color-text)" : "",
              },
              attrs: {
                disabled: this.isLoading || this.disabled,
              },
              on: {
                click: this.thumbsDownClick,
              },
            },
            [
              h("i", { 
                class: "fa fa-thumbs-down fa-lg",
                style: {
                  color: this.thumbsDownActive ? "var(--color-text)" : "",
                }
              }),
              h("span", { 
                style: { 
                  fontSize: "1.25rem", 
                  fontWeight: "bold",
                  color: this.thumbsDownActive ? "var(--color-text)" : "",
                } 
              }, `${Math.round(this.displayedThumbsDown)}`),
            ]
          ),
        ]),

        this.total > 0
          ? h("div", { class: "vote-stats mt-3 d-none" }, [
              h("small", {}, `Gesamt: ${this.total} Bewertungen`),
            ])
          : null,
      ]),
    ]);
  },
});
